import { millisecondsInMinute } from 'date-fns/constants';

type AnalyticsHandlerFn = (...args: unknown[]) => void;
type ObjectAnalyticsHandler = Record<string, AnalyticsHandlerFn>;
type AnalyticsFn = AnalyticsHandlerFn | ObjectAnalyticsHandler;
type ConsentOption = 'denied' | 'granted';
type ConsentCommand = 'default' | 'update';
type ConsentStorageKeys = 'ad_storage' | 'analytics_storage' | 'ad_personalization' | 'ad_user_data';

export interface ConsentMode {
  command: ConsentCommand;
  option: ConsentOption;
}

type ConsentFactory = {
  manager: string;
  handler?: string;
  consentStorageKeys: ConsentStorageKeys[];
  consentModesToSet: ConsentMode[];
  startTime: number;
  fallBackHandler?: () => void;
  additionalCheck?: () => boolean;
};

const getTagManager = (manager: string) => window[manager as keyof typeof window] as AnalyticsFn;

export const setConsentFactory = ({
  manager,
  handler: handlerKey,
  consentStorageKeys,
  consentModesToSet,
  startTime = Date.now(),
  fallBackHandler,
  additionalCheck,
}: ConsentFactory) => {
  if (typeof window === 'undefined') return;
  if (Date.now() - startTime >= millisecondsInMinute) {
    // eslint-disable-next-line no-console
    console.error(`Failed to load ${manager} within the max time limit.`);
    return;
  }
  let tagManager = getTagManager(manager);
  const handlerFn = handlerKey ? (tagManager as ObjectAnalyticsHandler)?.[handlerKey] : tagManager;
  const isHandlerReady = typeof handlerFn === 'function';

  if (!isHandlerReady) {
    fallBackHandler?.();
    tagManager = getTagManager(manager);
  }

  // Special handling for UET
  if (additionalCheck && !additionalCheck()) {
    setTimeout(() => {
      setConsentFactory({
        manager,
        handler: handlerKey,
        consentStorageKeys,
        consentModesToSet,
        startTime,
        fallBackHandler,
        additionalCheck,
      });
    }, CHECK_INTERVAL);
    return;
  }

  consentModesToSet.forEach(({ command, option }) => {
    ((handlerKey ? (tagManager as ObjectAnalyticsHandler)[handlerKey] : tagManager) as AnalyticsHandlerFn)?.(
      'consent',
      command,
      Object.fromEntries(consentStorageKeys.map(key => [key, option])),
    );
  });
};

const CHECK_INTERVAL = 1000;
